import { clsx } from 'clsx'
import { Loader2, X } from 'lucide-react'
import { forwardRef, useEffect, useId, useRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react'
import { twMerge } from 'tailwind-merge'
import { t } from '../lib/i18n.ts'

export const cn = (...a: Parameters<typeof clsx>) => twMerge(clsx(...a))

type BtnVariant = 'primary' | 'quiet' | 'outline' | 'danger' | 'ok'
const BTN: Record<BtnVariant, string> = {
  primary: 'bg-caneta text-white hover:bg-caneta-escura disabled:bg-tinta-3',
  quiet: 'bg-transparent text-caneta hover:bg-caneta-clara',
  outline: 'bg-folha text-tinta border border-pauta hover:border-caneta hover:text-caneta',
  danger: 'bg-folha text-margem border border-margem/40 hover:bg-margem hover:text-white',
  ok: 'bg-carimbo text-white hover:brightness-95',
}
export const Button = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { variant?: BtnVariant; busy?: boolean; size?: 'md' | 'sm' }>(
  function Button({ variant = 'primary', busy, size = 'md', className, children, disabled, ...p }, ref) {
    return (
      <button
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center gap-2 rounded-ficha font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-70',
          size === 'md' ? 'min-h-11 px-4 text-[1rem]' : 'min-h-9 px-3 text-[0.9rem]',
          BTN[variant],
          className,
        )}
        disabled={disabled || busy}
        {...p}
      >
        {busy && <Loader2 className="size-4 animate-spin" aria-hidden />}
        {children}
      </button>
    )
  },
)

export function Field({ label, hint, error, children, htmlFor }: { label: string; hint?: ReactNode; error?: string | null; children: ReactNode; htmlFor?: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-[0.95rem] font-semibold text-tinta">
        {label}
      </label>
      {children}
      {hint && !error && <p className="text-sm text-tinta-2">{hint}</p>}
      {error && (
        <p className="text-sm font-semibold text-margem" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}

const control = 'w-full min-h-11 rounded-ficha border border-pauta bg-folha px-3 text-tinta placeholder:text-tinta-3 focus:border-caneta focus:outline-none focus-visible:outline-none focus:ring-3 focus:ring-caneta/20'
export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input({ className, ...p }, ref) {
  return <input ref={ref} className={cn(control, className)} {...p} />
})
export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Select({ className, ...p }, ref) {
  return <select ref={ref} className={cn(control, 'pr-8', className)} {...p} />
})
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea({ className, ...p }, ref) {
  return <textarea ref={ref} className={cn(control, 'min-h-28 py-2 leading-relaxed', className)} {...p} />
})

type Tone = 'neutral' | 'caneta' | 'carimbo' | 'alerta' | 'margem'
const TONE: Record<Tone, string> = {
  neutral: 'bg-papel text-tinta-2 border-pauta',
  caneta: 'bg-caneta-clara text-caneta-escura border-caneta/20',
  carimbo: 'bg-carimbo-claro text-carimbo border-carimbo/25',
  alerta: 'bg-alerta-claro text-alerta border-alerta/25',
  margem: 'bg-margem/10 text-margem border-margem/30',
}
export function Badge({ tone = 'neutral', children, className }: { tone?: Tone; children: ReactNode; className?: string }) {
  return <span className={cn('inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[0.8rem] font-semibold whitespace-nowrap', TONE[tone], className)}>{children}</span>
}

export function Spinner({ label = t('Carregando') }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 py-8 text-tinta-2" role="status">
      <Loader2 className="size-5 animate-spin" aria-hidden />
      <span>{label}…</span>
    </div>
  )
}

export function Empty({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="rounded-ficha border border-dashed border-pauta bg-folha px-5 py-8">
      <p className="text-lg font-semibold">{title}</p>
      {children && <div className="mt-1 max-w-prose text-tinta-2">{children}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}

export function PageHeader({ title, children, back }: { title: string; children?: ReactNode; back?: ReactNode }) {
  return (
    <header className="mb-5 flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0">
        {back}
        <h1 className="text-[1.6rem] font-bold leading-tight tracking-[-0.01em] sm:text-[1.9rem]">{title}</h1>
      </div>
      {children && <div className="flex flex-wrap gap-2">{children}</div>}
    </header>
  )
}

export function Dialog({ open, onClose, title, children, wide }: { open: boolean; onClose: () => void; title: string; children: ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDialogElement>(null)
  const id = useId()
  useEffect(() => {
    const d = ref.current
    if (!d) return
    if (open && !d.open) {
      d.showModal()
      // showModal() focuses the first focusable element, the close button. With a mouse or keyboard, start on the
      // first field instead; on touch screens stay on the button, so the on-screen keyboard does not cover the form.
      if (window.matchMedia?.('(pointer: fine)').matches) d.querySelector<HTMLElement>('[data-dialog-body] :is(input:not([type=hidden]), select, textarea):not([disabled])')?.focus()
    }
    if (!open && d.open) d.close()
  }, [open])
  return (
    <dialog
      ref={ref}
      aria-labelledby={id}
      onClose={onClose}
      onClick={(e) => e.target === ref.current && onClose()}
      className={cn('m-auto w-[calc(100%-1.5rem)] rounded-ficha border border-pauta bg-folha p-0 text-tinta shadow-[0_18px_60px_-20px_rgba(24,35,58,0.45)] backdrop:bg-tinta/40', wide ? 'max-w-3xl' : 'max-w-lg')}
    >
      {open && (
        <div className="p-5 sm:p-6">
          <div className="mb-4 flex items-start justify-between gap-4">
            <h2 id={id} className="text-xl font-bold">
              {title}
            </h2>
            <button onClick={onClose} className="-m-2 rounded p-2 text-tinta-2 hover:text-tinta" aria-label={t('Fechar')}>
              <X className="size-5" />
            </button>
          </div>
          <div data-dialog-body>{children}</div>
        </div>
      )}
    </dialog>
  )
}

export function ErrorNote({ error }: { error: unknown }) {
  if (!error) return null
  const msg = error instanceof Error ? error.message : t('Algo deu errado.')
  return (
    <p className="rounded-ficha border border-margem/30 bg-margem/5 px-3 py-2 text-[0.95rem] text-margem" role="alert">
      {msg}
    </p>
  )
}

export function Row({ children, className, ...p }: { children: ReactNode; className?: string } & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('flex items-center gap-3 border-b border-pauta px-4 py-3 last:border-b-0', className)} {...p}>
      {children}
    </div>
  )
}

export function Sheet({ children, className, id }: { children: ReactNode; className?: string; id?: string }) {
  return <section id={id} className={cn('overflow-hidden rounded-ficha border border-pauta bg-folha', className)}>{children}</section>
}
